/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */
import type {
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  EditorConfig,
  ElementFormatType,
  LexicalEditor,
  LexicalNode,
  NodeKey,
  Spread
} from 'lexical';

import {
  DecoratorBlockNode,
  SerializedDecoratorBlockNode
} from '@lexical/react/LexicalDecoratorBlockNode';
import * as React from 'react';
import { TweetComponent } from './TweetComponent';

function $convertTweetElement(domNode: HTMLDivElement): DOMConversionOutput | null {
  const id = domNode.getAttribute('data-lexical-tweet-id');
  const width = domNode.getAttribute('data-lexical-tweet-width');
  const height = domNode.getAttribute('data-lexical-tweet-height');

  if (id) {
    const node = $createTweetNode(
      id,
      width ? parseInt(width, 10) : undefined,
      height ? parseInt(height, 10) : undefined
    );
    return { node };
  }
  return null;
}
export type SerializedTweetNode = Spread<
  {
    id: string;
    width?: number;
    height?: number;
  },
  SerializedDecoratorBlockNode
>;

export class TweetNode extends DecoratorBlockNode {
  __id: string;
  __width?: number;
  __height?: number;

  static getType(): string {
    return 'tweet';
  }

  static clone(node: TweetNode): TweetNode {
    return new TweetNode(node.__id, node.__width, node.__height, node.__format, node.__key);
  }

  static importJSON(serializedNode: SerializedTweetNode): TweetNode {
    const node = $createTweetNode(serializedNode.id, serializedNode.width, serializedNode.height);
    node.setFormat(serializedNode.format);
    return node;
  }

  setWidthAndHeight(width: number, height: number): void {
    const writable = this.getWritable();
    writable.__width = width;
    writable.__height = height;
  }

  exportJSON(): SerializedTweetNode {
    return {
      ...super.exportJSON(),
      id: this.__id,
      type: 'tweet',
      version: 1,
      width: this.__width,
      height: this.__height
    };
  }

  static importDOM(): DOMConversionMap<HTMLDivElement> | null {
    return {
      div: (domNode: HTMLDivElement) => {
        if (!domNode.hasAttribute('data-lexical-tweet-id')) {
          return null;
        }
        return {
          conversion: $convertTweetElement,
          priority: 2
        };
      }
    };
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('div');
    element.setAttribute('data-lexical-tweet-id', this.__id);
    element.setAttribute('data-lexical-tweet-width', String(this.__width));
    element.setAttribute('data-lexical-tweet-height', String(this.__height));
    const text = document.createTextNode(this.getTextContent());
    element.append(text);
    return { element };
  }

  constructor(
    id: string,
    width?: number,
    height?: number,
    format?: ElementFormatType,
    key?: NodeKey
  ) {
    super(format, key);
    this.__id = id;
    this.__width = width || 550;
    this.__height = height || 100;
  }

  getId(): string {
    return this.__id;
  }

  getTextContent(
    _includeInert?: boolean | undefined,
    _includeDirectionless?: false | undefined
  ): string {
    return `https://x.com/i/web/status/${this.__id}`;
  }

  decorate(editor: LexicalEditor, config: EditorConfig): JSX.Element {
    const embedBlockTheme = config.theme.embedBlock || {};
    const className = {
      base: embedBlockTheme.base + ' relative' || '',
      focus: embedBlockTheme.focus + ' relative' || ''
    };
    const format: ElementFormatType = 'center';
    return (
      <TweetComponent
        className={className}
        width={this.__width}
        height={this.__height}
        format={format} //this.format
        loadingComponent="Loading..."
        nodeKey={this.getKey()}
        tweetID={this.__id}
        isResizable={true}
        hideDeleteButton={false}
      />
    );
  }
}

export function $createTweetNode(tweetID: string, width?: number, height?: number): TweetNode {
  return new TweetNode(tweetID, width, height);
}

export function $isTweetNode(node: TweetNode | LexicalNode | null | undefined): node is TweetNode {
  return node instanceof TweetNode;
}
